import { Pencil } from "lucide-react";
import { useLang } from "@/i18n";

const AGENCY_LABELS = {
  master_fb: "Master F&B manages the printing agency end-to-end",
  own_agency: "Own local agency (Master F&B provides print-ready artwork & specs)",
};

const Row = ({ label, value }) => {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr] gap-3 py-2 border-b border-[#f0f0f0] last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#999999] pt-0.5">{t(label)}</span>
      <span className="text-sm text-[#000000] break-words">{value || "—"}</span>
    </div>
  );
};

const Section = ({ title, stepIndex, goToStep, children, testId }) => {
  const { t } = useLang();
  return (
    <div className="bg-white border border-[#e8e8e8] rounded-sm" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e8e8e8]">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#000000]">{t(title)}</span>
        <button
          onClick={() => goToStep(stepIndex)}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#dd260e] hover:underline"
          data-testid={`${testId}-edit`}
        >
          <Pencil className="w-3 h-3" /> {t("Edit")}
        </button>
      </div>
      <div className="px-5 py-3">{children}</div>
    </div>
  );
};

export const StepReview = ({ form, goToStep }) => {
  const { t } = useLang();
  return (
  <div className="space-y-4" data-testid="setup-step-review">
    <Section title="Market & Contacts" stepIndex={0} goToStep={goToStep} testId="setup-review-market">
      <Row label="Country" value={form.country ? t(form.country) : ""} />
      <Row label="BU Owner" value={`${form.bu_owner_name} — ${form.bu_owner_email}`} />
      <Row
        label="IT Liaison"
        value={form.it_liaison_name ? `${form.it_liaison_name}${form.it_liaison_email ? ` — ${form.it_liaison_email}` : ""}` : ""}
      />
      <Row label="Local SPOC" value={form.spoc_name ? `${form.spoc_name}${form.spoc_email ? ` — ${form.spoc_email}` : ""}` : ""} />
    </Section>

    <Section title="Brand & Localisation" stepIndex={1} goToStep={goToStep} testId="setup-review-brand">
      <Row label="Default language" value={form.default_language} />
      <Row label="Additional languages" value={form.additional_languages} />
      <Row label="Currency" value={form.currency} />
      <Row
        label="Brand color"
        value={
          form.brand_color ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-4 h-4 rounded-sm border border-[#e8e8e8] inline-block" style={{ backgroundColor: form.brand_color }} />
              {form.brand_color}
            </span>
          ) : (
            ""
          )
        }
      />
      <Row label="Brand notes" value={form.brand_notes} />
    </Section>

    <Section title="QR Menu Setup" stepIndex={2} goToStep={goToStep} testId="setup-review-qrmenu">
      <Row label="Estimated outlets" value={form.outlets_estimate} />
      <Row label="Segments" value={form.segments.map((s) => t(s)).join(", ")} />
      <Row label="Print agency" value={form.print_agency ? t(AGENCY_LABELS[form.print_agency]) : ""} />
      {form.print_agency === "own_agency" && <Row label="Print contact" value={form.print_contact} />}
      <Row label="Go-live target" value={form.golive_target} />
      <Row label="Requirements" value={form.additional_requirements} />
    </Section>

    <Section title="Team & Touchpoints" stepIndex={3} goToStep={goToStep} testId="setup-review-team">
      <Row
        label="Training"
        value={
          form.training_assignments.filter((p) => p.name || p.email).length
            ? form.training_assignments
                .filter((p) => p.name || p.email)
                .map((p) => `${p.role ? `${p.role}: ` : ""}${p.name}${p.email ? ` — ${p.email}` : ""}`)
                .join(" · ")
            : ""
        }
      />
      <Row
        label="Touchpoints"
        value={
          form.main_touchpoints.filter((p) => p.name || p.email).length
            ? form.main_touchpoints
                .filter((p) => p.name || p.email)
                .map((p) => `${p.role ? `${p.role}: ` : ""}${p.name}${p.email ? ` — ${p.email}` : ""}`)
                .join(" · ")
            : ""
        }
      />
      <Row label="Banner plan" value={form.banner_plan} />
    </Section>

    <Section title="Assets" stepIndex={4} goToStep={goToStep} testId="setup-review-assets">
      {form.asset_files.length === 0 ? (
        <p className="text-sm text-[#999999] py-1">{t("No files uploaded")}</p>
      ) : (
        form.asset_files.map((f) => <Row key={f.file_id} label="File" value={f.filename} />)
      )}
    </Section>

    <p className="text-xs text-[#999999] leading-relaxed pt-1">
      {t("On submit, your setup is delivered to the Master F&B onboarding team (info@master-fb.com). All data is treated as confidential under the existing NDA between Coca-Cola HBC and Master F&B.")}
    </p>
  </div>
  );
};
